/**
 * The audit log's labels and target links.
 *
 * A hand port of `AuditLimits.ActorKindLabel`/`CategoryLabel`/`OutcomeLabel`/`AllCategories` and
 * `AuditTargets.TryParse` from `Contracts/Audit.cs`. Those are the one place the API, the CSV export
 * and the page agree on how a row is described, so this copy is pinned to the generated enums by
 * `audit.test.ts`: a category appended server-side becomes a failing test, not a filter option nobody
 * notices is missing.
 */
import { AuditActorKind, AuditCategory, AuditOutcome, AuditTargets } from './contracts.gen.js';

export function actorKindLabel(kind: AuditActorKind): string {
  switch (kind) {
    case AuditActorKind.Staff:
      return 'Staff';
    case AuditActorKind.Portal:
      return 'Customer portal';
    case AuditActorKind.Device:
      return 'Device';
    case AuditActorKind.System:
      return 'System';
    default:
      return 'Anonymous';
  }
}

export function categoryLabel(category: AuditCategory): string {
  switch (category) {
    case AuditCategory.SignIn:
      return 'Sign-in';
    case AuditCategory.Account:
      return 'Accounts';
    case AuditCategory.Customer:
      return 'Customers';
    case AuditCategory.Device:
      return 'Devices';
    case AuditCategory.RemoteAccess:
      return 'Remote access';
    case AuditCategory.Billing:
      return 'Billing';
    case AuditCategory.Ticket:
      return 'Tickets';
    case AuditCategory.Catalog:
      return 'Catalog';
    case AuditCategory.Expense:
      return 'Expenses';
    case AuditCategory.Software:
      return 'Software';
    case AuditCategory.Monitoring:
      return 'Monitoring';
    case AuditCategory.Discovery:
      return 'Discovery';
    case AuditCategory.Settings:
      return 'Settings';
    case AuditCategory.Backup:
      return 'Backups';
    case AuditCategory.Mail:
      return 'Mail';
    case AuditCategory.Security:
      return 'Security';
    case AuditCategory.Portal:
      return 'Customer portal';
    case AuditCategory.KnowledgeBase:
      return 'Knowledge base';
    case AuditCategory.Forum:
      return 'Forum';
    case AuditCategory.Server:
      return 'Server';
    case AuditCategory.Sales:
      return 'Leads and marketing';
    default:
      return 'Other';
  }
}

export function outcomeLabel(outcome: AuditOutcome): string {
  return outcome === AuditOutcome.Refused ? 'Refused' : outcome === AuditOutcome.Failed ? 'Failed' : 'OK';
}

/** The actor kinds, in the order the filter lists them. */
export const ACTOR_KINDS: readonly AuditActorKind[] = [
  AuditActorKind.Staff,
  AuditActorKind.Portal,
  AuditActorKind.Device,
  AuditActorKind.Anonymous,
  AuditActorKind.System
];

/** Every category, in the order the filter lists them - the C# `AllCategories` order. */
export const ALL_CATEGORIES: readonly AuditCategory[] = [
  AuditCategory.SignIn, AuditCategory.Account, AuditCategory.Security,
  AuditCategory.Customer, AuditCategory.Sales, AuditCategory.Device, AuditCategory.RemoteAccess,
  AuditCategory.Software, AuditCategory.Monitoring, AuditCategory.Discovery,
  AuditCategory.Ticket, AuditCategory.Billing, AuditCategory.Catalog, AuditCategory.Expense,
  AuditCategory.Portal, AuditCategory.KnowledgeBase, AuditCategory.Forum,
  AuditCategory.Settings, AuditCategory.Backup, AuditCategory.Mail,
  AuditCategory.Server, AuditCategory.Other
];

/** `"device:12"` -> `{ kind: 'device', id: 12 }`, or null for anything else. */
export function parseTarget(target: string | null | undefined): { kind: string; id: number } | null {
  if (!target) return null;
  const colon = target.indexOf(':');
  if (colon <= 0 || colon === target.length - 1) return null;
  const id = Number(target.slice(colon + 1));
  return Number.isInteger(id) ? { kind: target.slice(0, colon), id } : null;
}

/** Where a row's "Open" button goes, and what it says - only for targets that are a page here. */
export function targetLink(target: string | null | undefined): { label: string; path: string } | null {
  const parsed = parseTarget(target);
  if (!parsed) return null;
  switch (parsed.kind) {
    case AuditTargets.Device:
      return { label: 'Open device', path: `/devices/${parsed.id}` };
    case AuditTargets.Customer:
      return { label: 'Open customer', path: `/customers/${parsed.id}` };
    case AuditTargets.Invoice:
      return { label: 'Open invoice', path: `/invoices/${parsed.id}` };
    case AuditTargets.Ticket:
      return { label: 'Open ticket', path: `/tickets/${parsed.id}` };
    case AuditTargets.Monitor:
      return { label: 'Open monitor', path: `/monitors/${parsed.id}` };
    case AuditTargets.Lead:
      return { label: 'Open lead', path: `/leads/${parsed.id}` };
    case AuditTargets.Campaign:
      return { label: 'Open campaign', path: `/marketing/${parsed.id}` };
    default:
      return null;
  }
}
